import styled from '@emotion/styled';
import { ErrorRounded } from '@mui/icons-material';
import { Container } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import MessageInputField from '../components/CaseDetails/CaseMessages/MessageInputField';
import MessageList from '../components/CaseDetails/CaseMessages/MessageList';
import AuthStatusHandler, { AuthStatus } from '../components/Messasing/AuthStatusHandler';
import { publicGet, publicPost } from '../lib/api';
import { CaseMessageModel, NewMessageModel } from '../lib/models/CaseMessageModel';
import useTrackEvent from '../lib/hooks/use-track-event';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import useTrackException from '../lib/hooks/use-track-exception';
import { getEnv } from '../lib/helper/get-env';

type Message = {
    type: string;
    data: {
        clientName: string;
        caseId: string;
        caseNumber: string;
        userId: string;
        token: string;
    };
};

const MessagingPage = () => {
    const trackPageView = useTrackPageView();
    const trackEvent = useTrackEvent();
    const trackException = useTrackException();
    const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.NoTokenYet);

    const [caseMessagesData, setCaseMessagesData] = useState<CaseMessageModel[] | null>(null);
    const [messageData, setMessageData] = useState<Message | null>(null);

    const [isGetMsgApiCalled, setIsGetMsgApiCalled] = useState(false);
    const [, setIsSaveMsgApiCalled] = useState(false);
    const [outOfCrm, setOutOfCrm] = useState(false);

    const isMessageFromCrm = () =>
        messageData ? Object.prototype.hasOwnProperty.call(messageData, 'type') : false;

    const checkAuth = useCallback(async () => {
        if (!messageData) {
            setAuthStatus(AuthStatus.NoTokenYet);
            return;
        }

        if (!messageData.data?.token) {
            setAuthStatus(AuthStatus.NoToken);
            return;
        }

        try {
            const { status } = await publicGet(
                'auth/check',
                setAuthHeader(messageData.data?.token)
            );

            if (status >= 200 && status <= 299) setAuthStatus(AuthStatus.Success);
            else setAuthStatus(AuthStatus.Fail);
        } catch (error) {
            setAuthStatus(AuthStatus.Fail);
            console.log(error);
        }
    }, [messageData]);

    const getCaseMessages = useCallback(
        async (
            caseId: string | undefined,
            clientName: string | undefined,
            userId: string | undefined
        ) => {
            if (!messageData) return;

            const { token } = messageData.data;

            if (authStatus === AuthStatus.Fail) return;

            try {
                const result = await publicGet<CaseMessageModel[]>(
                    `messaging/getMessages?crmClient=${clientName}&caseId=${caseId}&userId=${userId}`,
                    setAuthHeader(token)
                );

                setCaseMessagesData(result.data);
                setIsGetMsgApiCalled(true);
            } catch (error) {
                toast.error('An error occurred while getting the case messages', {
                    autoClose: 5000
                });
                console.log(error);
            }
        },
        [authStatus, messageData]
    );

    const handleSave = async (messageText: string) => {
        if (!messageData) return;

        const { caseId, clientName, userId, token } = messageData.data;
        const message: NewMessageModel = {
            crmName: clientName ? clientName : '',
            crmCaseId: caseId ? caseId : '',
            createdBy: userId ? userId : '',
            text: messageText
        };
        try {
            const result = await publicPost(`messaging/postmessage`, message, setAuthHeader(token));
            if (result.data != null) {
                toast.success('Message sent', { autoClose: 5000 });
                setIsSaveMsgApiCalled(true);
                trackEvent('Case message is sent (CRM)', { sentBy: userId });
                await getCaseMessages(caseId, clientName, userId);
            }
        } catch (error) {
            trackException(error as Error, { sentBy: userId });
            toast.error('An error occurred while sending the message', { autoClose: 5000 });
            console.log(error);
        }
    };

    useEffect(() => {
        trackPageView('Casse Messaging Page (CRM)');
        if (window.opener) window.opener.postMessage('mdNavMessagingPageLoaded', '*');
        else setOutOfCrm(true);
    }, [trackPageView]);

    useEffect(() => {
        const postMessageHandler = (event: MessageEvent) => {
            if (event.data.type && event.data.type === 'messagingInfoDispatch')
                setMessageData(event.data);
        };

        window.addEventListener('message', postMessageHandler);

        return () => {
            window.removeEventListener('message', postMessageHandler);
        };
    }, []);

    useEffect(() => {
        if (!outOfCrm && messageData) {
            document.title = `${messageData.data.clientName} - ${messageData.data.caseNumber}`;
        }
    }, [messageData, outOfCrm]);

    useEffect(() => {
        let refreshTimeoutRef: NodeJS.Timeout | undefined;
        getEnv('REACT_APP_REFRESH_TIMEOUT')
            .then((refreshTimeout) => Number(refreshTimeout))
            .then((refreshTimeout) => {
                const interval = setInterval(() => {
                    if (messageData) {
                        if (authStatus === AuthStatus.Success) {
                            getCaseMessages(
                                messageData.data.caseId,
                                messageData.data.clientName,
                                messageData.data.userId
                            );
                        }
                    }
                }, refreshTimeout * 1000);
                refreshTimeoutRef = interval;
            });
        return () => {
            if (refreshTimeoutRef) clearInterval(refreshTimeoutRef);
        };
    }, [messageData, authStatus, getCaseMessages]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    useEffect(() => {
        if (messageData) {
            if (authStatus === AuthStatus.Success) {
                getCaseMessages(
                    messageData.data.caseId,
                    messageData.data.clientName,
                    messageData.data.userId
                );
            }
        }
    }, [messageData, authStatus, getCaseMessages]);

    useEffect(() => {
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
        };
        document.addEventListener('contextmenu', handleContextMenu);
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    return outOfCrm ? (
        <AuthStatusHandler
            IconComp={<ErrorRounded color="error" sx={{ fontSize: 80 }} />}
            message="This page is only allowed to be opened from CRM"
        />
    ) : !isMessageFromCrm() ? (
        <AuthStatusHandler
            IconComp={<CircularProgress sx={{ fontSize: 80 }} />}
            message="Waiting data from CRM..."
        />
    ) : (
        <>
            {authStatus === AuthStatus.Success ? (
                isGetMsgApiCalled && !caseMessagesData ? (
                    <AuthStatusHandler
                        IconComp={<ErrorRounded color="error" sx={{ fontSize: 80 }} />}
                        message="No message data exist in MD Navigator"
                    />
                ) : (
                    <MessagingContainer maxWidth={false}>
                        <MessageInputField handleSave={handleSave} disabled={false} caseStage="" />
                        <MessageList data={caseMessagesData} />
                    </MessagingContainer>
                )
            ) : authStatus === AuthStatus.Fail ? (
                <AuthStatusHandler
                    IconComp={<ErrorRounded color="error" sx={{ fontSize: 80 }} />}
                    message="Error authorizing service account"
                />
            ) : authStatus === AuthStatus.NoToken ? (
                <AuthStatusHandler
                    IconComp={<ErrorRounded color="error" sx={{ fontSize: 80 }} />}
                    message="No token specified for this request"
                />
            ) : authStatus === AuthStatus.NoTokenYet ? (
                <AuthStatusHandler
                    IconComp={<CircularProgress sx={{ fontSize: 80 }} />}
                    message="Authorizing..."
                />
            ) : (
                <AuthStatusHandler
                    IconComp={<CircularProgress sx={{ fontSize: 80 }} />}
                    message="Loading..."
                />
            )}
        </>
    );
};

const setAuthHeader: (token: string) => AxiosRequestConfig = (token: string) => ({
    headers: {
        Authorization: 'Bearer ' + token
    }
});

const MessagingContainer = styled(Container)`
    margin-bottom: 2;
    margin-top: 1.25rem;
    padding: 0 0.813rem;
`;

export default MessagingPage;
